import axios from 'axios';

const BASE = '/api/v1/effective-capital';

export const effectiveCapitalApi = {
  getSummary: () => axios.get(`${BASE}/summary`).then((r) => r.data),
  getDoDelta: (todayDate, yesterdayDate) =>
    axios.get(`${BASE}/dod-delta`, { params: { todayDate, yesterdayDate } }).then((r) => r.data),
  listAccounts: (includeRetired = false) => axios.get(
    `${BASE}/accounts`,
    includeRetired ? { params: { includeRetired: 'true' } } : undefined
  ).then((r) => r.data),
  updatePaymentDue: (plaidAccountId, paymentDueDay) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/payment-due`, { paymentDueDay }).then((r) => r.data),
  updateCustomName: (plaidAccountId, customName) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/custom-name`, { customName }).then((r) => r.data),
  updateEntity: (plaidAccountId, entity) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/entity`, { entity }).then((r) => r.data),
  setExcludeFromTotals: (plaidAccountId, excludeFromTotals) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/exclude-from-totals`, { excludeFromTotals }).then((r) => r.data),
  setAccountRetired: (plaidAccountId, retired) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/retirement`, { retired }).then((r) => r.data),
  listBalanceOverrides: () =>
    axios.get(`${BASE}/accounts/balance-overrides`).then((r) => r.data),
  setBalanceOverride: (plaidAccountId, overrideBalance) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/balance-override`, { overrideBalance }).then((r) => r.data),
  setLimitOverride: (plaidAccountId, limitOverride) =>
    axios.patch(`${BASE}/accounts/${plaidAccountId}/limit-override`, { limitOverride }).then((r) => r.data),
  listTransactions: (params) =>
    axios.get(`${BASE}/transactions`, { params }).then((r) => r.data),
  getTransactionsSyncStatus: () =>
    axios.get(`${BASE}/transactions/sync-status`).then((r) => r.data),
  getTransactionKpis: (params) =>
    axios.get(`${BASE}/transactions/kpis`, { params }).then((r) => r.data),
  getSpendingByCategory: (params) =>
    axios.get(`${BASE}/transactions/spending-by-category`, { params }).then((r) => r.data),
  getMonthlyTotals: (params) =>
    axios.get(`${BASE}/transactions/monthly-totals`, { params }).then((r) => r.data),
  syncTransactions: (startDate, endDate) =>
    axios.post(`${BASE}/transactions/sync`, { startDate, endDate }).then((r) => r.data),
  syncTransactionsIncremental: () =>
    axios.post(`${BASE}/transactions/sync-incremental`).then((r) => r.data),
  bulkRecategorize: () =>
    axios.post(`${BASE}/transactions/bulk-recategorize`).then((r) => r.data),
  getCashflowExport: () => axios.get(`${BASE}/cashflow-export`).then((r) => r.data),
  getEcHistory: (params, config = {}) =>
    axios.get(`${BASE}/history`, { ...config, params }).then((r) => r.data),
  saveStatementBalance: (plaidAccountId, statementBalance) =>
    axios.patch(`${BASE}/cashflow-export/${plaidAccountId}/statement-balance`, { statementBalance }).then((r) => r.data),
  syncLiabilities: () => axios.post(`${BASE}/cashflow-export/sync-liabilities`).then((r) => r.data),
  createLinkToken: () => axios.post(`${BASE}/plaid/link-token`).then((r) => r.data),
  createDepositoryLinkToken: () => axios.post(`${BASE}/plaid/link-token-depository`).then((r) => r.data),
  createUpdateModeLinkToken: (itemId, accountSelection = false) =>
    axios.post(`${BASE}/plaid/link-token-update/${itemId}`, { accountSelection }).then((r) => r.data),
  exchangeToken: (body) => axios.post(`${BASE}/plaid/exchange-token`, body).then((r) => r.data),
  syncBalances: () => axios.post(`${BASE}/plaid/sync-balances`).then((r) => r.data),
  detectErrors: () => axios.post(`${BASE}/plaid/detect-errors`).then((r) => r.data),
  clearItemError: (itemId) => axios.post(`${BASE}/plaid/items/${itemId}/clear-error`).then((r) => r.data),
  getPlaidItems: () => axios.get(`${BASE}/plaid/items`).then((r) => r.data),
  getPlaidItemsAll: () => axios.get(`${BASE}/plaid/items-all`).then((r) => r.data),
  removePlaidItem: (itemId) => axios.delete(`${BASE}/plaid/items/${itemId}`).then((r) => r.data),

  // Historical snapshots / account detail
  getEcSnapshotForDate: (date) => axios.get(`${BASE}/snapshots/${date}`).then((r) => r.data),
  getAccountDetailForDate: (date) => axios.get(`${BASE}/account-detail/${date}`).then((r) => r.data),
  getOperatingCreditForDate: (date) =>
    axios.get(`${BASE}/operating-credit/for-date`, { params: { date } }).then((r) => r.data),

  // Manual receivables (Shopify / Amazon / other)
  getReceivablesForDate: (date) =>
    axios.get(`${BASE}/receivables/for-date`, { params: { date } }).then((r) => r.data),
  getReceivablesLatestBefore: (date) =>
    axios.get(`${BASE}/receivables/latest-before`, { params: { date } }).then((r) => r.data),
  upsertManualReceivable: (body) => axios.post(`${BASE}/receivables`, body).then((r) => r.data),

  // Operating credit
  updateOperatingCredit: (id, fields) =>
    axios.patch(`${BASE}/operating-credit/${id}`, fields).then((r) => r.data),

  // Live Shopify / Amazon balances
  getShopifyBalance: () => axios.get(`${BASE}/shopify/balance`).then((r) => r.data),
  getAmazonBalance: () => axios.get(`${BASE}/amazon/balance`).then((r) => r.data),

  // Manual overrides
  getDeferredOverride: () => axios.get(`${BASE}/amazon/deferred-override`).then((r) => r.data),
  setDeferredOverride: (amount) => axios.put(`${BASE}/amazon/deferred-override`, { amount }).then((r) => r.data),
  getShopifyTotalOverride: () => axios.get(`${BASE}/shopify/total-override`).then((r) => r.data),
  setShopifyTotalOverride: (amount) => axios.put(`${BASE}/shopify/total-override`, { amount }).then((r) => r.data),
  getAmazonTotalOverride: () => axios.get(`${BASE}/amazon/total-override`).then((r) => r.data),
  setAmazonTotalOverride: (amount) => axios.put(`${BASE}/amazon/total-override`, { amount }).then((r) => r.data),
  getFlexCreditOverride: () => axios.get(`${BASE}/flex-credit/override`).then((r) => r.data),
  setFlexCreditOverride: (amount) => axios.put(`${BASE}/flex-credit/override`, { amount }).then((r) => r.data),

  exportCapitalSummaryPdf: async (date) => {
    const res = await axios.get(`${BASE}/export-pdf`, {
      params: date ? { date } : undefined,
      responseType: 'blob',
    });
    return res.data;
  },
};
